/**
 * @Description:  验证码工具方法
 * @Author:  NvDsper
 * @Date: 2026-03-25 10:29:29
 */
import { getAppLocale, toTencentCaptchaLang } from '@repo/common-utils/utils/appLocale'

/** 腾讯验证码 SDK（浏览器端动态插入） */
export const TENCENT_CAPTCHA_SCRIPT_URL = 'https://turing.captcha.qcloud.com/TCaptcha.js'

let tencentCaptchaScriptPromise: Promise<void> | null = null

/**
 * 将指定地址的外部脚本插入到 document.body 末尾，并等待加载完成。
 * 同一 src 重复调用会复用同一次加载 Promise，避免重复插入。
 */
export function appendScriptToBody(src: string): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('appendScriptToBody: 非浏览器环境，无 document'))
  }

  const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
  if (existing) {
    return new Promise((resolve, reject) => {
      if (existing.dataset.loadState === 'done') {
        resolve()
        return
      }
      if (existing.dataset.loadState === 'error') {
        reject(new Error(`脚本加载失败: ${src}`))
        return
      }
      const onLoad = () => {
        existing.dataset.loadState = 'done'
        resolve()
      }
      const onError = () => {
        existing.dataset.loadState = 'error'
        reject(new Error(`脚本加载失败: ${src}`))
      }
      existing.addEventListener('load', onLoad, { once: true })
      existing.addEventListener('error', onError, { once: true })
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.dataset.loadState = 'loading'
    script.onload = () => {
      script.dataset.loadState = 'done'
      resolve()
    }
    script.onerror = () => {
      script.dataset.loadState = 'error'
      reject(new Error(`脚本加载失败: ${src}`))
    }
    document.body.appendChild(script)
  })
}

/** 加载腾讯验证码 TCaptcha.js（已存在全局 TencentCaptcha 时直接 resolve） */
export function loadTencentCaptchaScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('loadTencentCaptchaScript: 非浏览器环境'))
  }
  if (window.TencentCaptcha) {
    return Promise.resolve()
  }
  if (!tencentCaptchaScriptPromise) {
    tencentCaptchaScriptPromise = appendScriptToBody(TENCENT_CAPTCHA_SCRIPT_URL).catch((err) => {
      tencentCaptchaScriptPromise = null
      throw err
    })
  }
  return tencentCaptchaScriptPromise
}

const tencentCaptchaService = (callback: any) =>
  new window.TencentCaptcha(
    '2013119836',
    (res: any) => {
      if (callback) {
        callback(res)
      }
    },
    {
      // enableDarkMode: localStorage.getItem('theme') == 'dark' ? 'force' : false,
      needFeedBack: false,
      userLanguage: toTencentCaptchaLang(getAppLocale()),
    }
  )

export function tencentCaptcha(): Promise<string> {
  return loadTencentCaptchaScript().then(
    () =>
      new Promise<string>((res, rej) => {
        const captcha = tencentCaptchaService(
          ({ ret, ticket, randstr, ...otherParameters }: any) => {
            if (ret === 0) {
              res(`${ticket} ${randstr}`)
            } else {
              rej({ ret, ...otherParameters })
            }
          }
        )
        captcha.show()
      })
  )
}
